import { Dialog, Transition } from "@headlessui/react";
import { TrashIcon, XIcon } from "@heroicons/react/outline";
import { LocationMarkerIcon, PlusIcon } from "@heroicons/react/solid";
import { Field, FormikHelpers, FormikProps } from "formik";
import {
  every,
  find,
  get,
  isEmpty,
  toLower,
  values,
  without,
  words
} from "lodash";
import { stringifyUrl } from "query-string";
import Tooltip from "rc-tooltip";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";
import { useLocations } from ".";
import AlertMessage from "../../components/AlertMessage";
import ContactComponent from "../../components/ContactComponent";
import DropdownSearch from "../../components/forms/DropdownSearch";
import FormProvider from "../../components/forms/FormProvider";
import HandleFieldChange from "../../components/forms/HandleFieldChange";
import Input from "../../components/forms/Input";
import InfoIcon from "../../components/icons/InfoIcon";
import UpgradeHotspotsModal from "../../components/modals/UpgradeHotspotsModal";
import { getPlaceCountryCode } from "../../components/QuickSearchModal";
import ContactFormSidebar from "../../pages/Workspace/ContactsData/ContactFormSidebar";
import classNames from "../../utils/classNames";
import { avatarsApi } from "../../utils/constants";
import {
  GeoResult,
  getDefaultGeoService,
  isGeoResult
} from "../../utils/geoservices";
import { GeoServices } from "../../utils/geoservices/geoservices";
import getCoordinates, {
  getUnshortenedGmapsUrl
} from "../../utils/getCoordinates";
import {
  Contact,
  HotspotLocation,
  PaymentStep,
  PlanType
} from "../../utils/types";
import useMapCenter, { Coordinates } from "../../utils/useMapCenter";
import useMapConfig from "../../utils/useMapConfig";
import usePrevious from "../../utils/usePrevious";
import { useBillingPlans } from "../billingPlans";
import { useContacts } from "../contacts";
import { useGoogleMapsWrapper } from "../googleMaps";
import { ModalConfig } from "../toast/ToastProvider";
import { useWorkspaces } from "../workspaces";

interface Props {
  config: EditLocationConfig;
  setConfig: React.Dispatch<React.SetStateAction<EditLocationConfig>>;
}

const LocationFormSidebar: React.FC<Props> = ({ config, setConfig }) => {
  const {
    locations,
    addLocation,
    updateLocation,
    setSelectPointsOnMapConfig,
    findLocationIdByCoordinates,
    locationsLimitExceeded
  } = useLocations();
  const { currentPlan, setPaymentStep } = useBillingPlans();
  const { currentWorkspace } = useWorkspaces();
  const { contacts, fetchContacts } = useContacts();
  const history = useHistory();
  const { status } = useGoogleMapsWrapper();
  const [openContactFormSidebar, setOpenContactFormSidebar] = useState(false);
  const [linkedContactUuids, setLinkedContactUuids] = useState<string[]>([]);
  const [center, boundaries, setCenter, setZoom] = useMapCenter((state) => [
    state.center,
    state.boundaries,
    state.setCenter,
    state.setZoom
  ]);
  const distanceUnit = useMapConfig((state) => state.distanceUnit);
  const getAppearanceDistanceValue = useMapConfig(
    (state) => state.getAppearanceDistanceValue
  );
  const getDistanceValueInMeters = useMapConfig(
    (state) => state.getDistanceValueInMeters
  );
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [location, setLocation] = useState<LocationType | undefined>();
  const dialogRef = useRef(null);

  const prevLocation = usePrevious(config?.location);
  useEffect(() => {
    if (
      config?.location &&
      isHotspotLocation(config.location) &&
      config.location !== prevLocation
    ) {
      setLocation(config.location);
      setLinkedContactUuids(config.location?.linked_contact_ids || []);
    }
  }, [config?.location, prevLocation]);

  const closeSidebar = useCallback(
    (location?: any) => {
      setConfig({ ...config, show: false, location });
      setLocation(undefined);
      setLinkedContactUuids([]);
    },
    [config, setConfig]
  );
  const [upgradeHotspotsConfig, setUpgradeHotspotsConfig] =
    useState<ModalConfig>({
      open: false
    });

  const toggleEditContactForm = useCallback(
    (contact: Contact | undefined) => {
      if (contact && !linkedContactUuids.includes(contact.uuid)) {
        setLinkedContactUuids(linkedContactUuids.concat(contact.uuid));
      }

      setOpenContactFormSidebar(!openContactFormSidebar);
    },
    [linkedContactUuids, openContactFormSidebar]
  );

  const convertLocationIntoFormValues = useCallback(
    (location: HotspotLocation): FormValues => ({
      name: location.name,
      place: location.metadata ? JSON.parse(location.metadata) : undefined,
      address: location.address ?? "",
      lat: location.lat,
      lng: location.lng,
      height: getAppearanceDistanceValue(
        location.height ?? DEFAULT_HEIGHT_IN_METERS,
        false
      )
    }),
    [getAppearanceDistanceValue]
  );

  const getEmptyFormValues = useCallback(
    () => ({
      name: "",
      place: undefined,
      address: "",
      lat: "",
      lng: "",
      height: getAppearanceDistanceValue(DEFAULT_HEIGHT_IN_METERS, false)
    }),
    [getAppearanceDistanceValue]
  );

  const initialValues = useMemo(() => {
    if (!config?.location) return getEmptyFormValues();

    if (isHotspotLocation(config.location)) {
      return convertLocationIntoFormValues(config.location);
    } else if (isGeoResult(config.location)) {
      return convertGeoResultIntoFormValues(config.location);
    }

    return convertCoordinatesIntoFormValues(config.location);
  }, [convertLocationIntoFormValues, getEmptyFormValues, config?.location]);

  const onSubmit = useCallback(
    async (
      { name, place, address, lat, lng, height }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      const hotspotLocation = findLocationIdByCoordinates(lat, lng);

      if (hotspotLocation && !location) {
        setErrorMessage("Location already exists");

        return;
      }

      const locationToSave = {
        name,
        address,
        lat,
        lng,
        height: getDistanceValueInMeters(height),
        metadata: place ? JSON.stringify(place) : null,
        contacts_ids: linkedContactUuids
      };

      let apiResponse;

      if (
        config?.location &&
        isHotspotLocation(config.location) &&
        config.location.uuid
      ) {
        apiResponse = await updateLocation(
          config.location.uuid,
          locationToSave
        );
      } else {
        apiResponse = await addLocation(locationToSave);
      }

      if (
        !isEmpty(linkedContactUuids) ||
        (isEmpty(linkedContactUuids) &&
          !isEmpty(get(config?.location, "linked_contact_ids", [])))
      ) {
        await fetchContacts();
      }

      if (apiResponse?.status) {
        closeSidebar();
        config?.onSuccess && config?.onSuccess(apiResponse.data);
      } else {
        setErrorMessage(
          apiResponse?.message ? apiResponse?.message : "Something went wrong"
        );
      }

      formikHelpers.setSubmitting(false);
    },
    [
      findLocationIdByCoordinates,
      location,
      getDistanceValueInMeters,
      linkedContactUuids,
      config,
      updateLocation,
      addLocation,
      fetchContacts,
      closeSidebar
    ]
  );

  const fetchPlaces = useCallback(
    async (searchInput: string | undefined) => {
      if (!searchInput || searchInput.trim().length <= 4) {
        return [];
      }

      const expandedGmapsUrl = await getUnshortenedGmapsUrl(searchInput);

      const coordinates = await getCoordinates(expandedGmapsUrl ?? searchInput);

      const service = getDefaultGeoService(
        status.current,
        !!expandedGmapsUrl ? GeoServices.GMAPS : null
      );

      try {
        const results = await service.debouncedSearch(
          coordinates || expandedGmapsUrl || searchInput,
          {
            center,
            boundaries
          }
        );

        if (coordinates && results && !isEmpty(results)) {
          setConfig({
            ...config,
            show: true,
            location: {
              ...results.filter(
                (item) =>
                  item.types.includes("plus_code") ||
                  item.types.includes("postcode")
              )[0],
              center: coordinates
            }
          });

          return [];
        }

        return results || [];
      } catch (error) {
        return [];
      }
    },
    [boundaries, center, config, setConfig, status]
  );

  const getContacts = useCallback(
    (searchText: string | undefined) => {
      const filteredContacts = values(contacts).filter((contact) => {
        if (linkedContactUuids.includes(contact.uuid)) {
          return false;
        }

        if (searchText) {
          const searchInputWords = words(searchText, /[^, ]+/g).map(toLower);

          return every(
            searchInputWords,
            (word) =>
              toLower(contact.name).includes(word) ||
              toLower(contact.email || "").includes(word)
          );
        }
        return true;
      });

      return filteredContacts.slice(0, 10);
    },
    [contacts, linkedContactUuids]
  );

  const isAbleToAddLocation = useMemo(
    () =>
      !locationsLimitExceeded ||
      (config.location &&
        isHotspotLocation(config.location) &&
        !!config.location.uuid) ||
      false,
    [config.location, locationsLimitExceeded]
  );

  const limitExceededAlertMessage = useMemo(() => {
    if (isAbleToAddLocation || !currentWorkspace) {
      return null;
    }

    const { allowed_locations } = currentWorkspace.active_plan;

    return (
      <AlertMessage
        type="warning"
        title={
          <span>
            This workspace has the {currentPlan?.name} plan which is limited to{" "}
            {allowed_locations} locations. You are trying to add a new location
            resulting in {allowed_locations + 1} locations, which is not
            allowed. Please{" "}
            <b
              className="font-bold dark:text-hotspotty-300 hover:underline cursor-pointer"
              onClick={() => {
                if (currentPlan?.type !== PlanType.community) {
                  setUpgradeHotspotsConfig({ open: true });

                  return;
                }

                history.push("/pricing");
                closeSidebar();
                setPaymentStep(PaymentStep.plans);
              }}
            >
              increase your limits
            </b>{" "}
            (every paid hotspot and simulation comes with two locations) or
            remove locations you no longer need.
          </span>
        }
      />
    );
  }, [
    isAbleToAddLocation,
    currentWorkspace,
    currentPlan?.name,
    currentPlan?.type,
    history,
    closeSidebar,
    setPaymentStep
  ]);

  const checkCanshow = useCallback(() => {
    if (config.show || upgradeHotspotsConfig.open) return true;

    return false;
  }, [config.show, upgradeHotspotsConfig.open]);

  return (
    <>
      <UpgradeHotspotsModal
        config={upgradeHotspotsConfig}
        setConfig={setUpgradeHotspotsConfig}
      />

      <Transition.Root show={checkCanshow()} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-20 overflow-hidden"
          open={checkCanshow()}
          onClose={() => {
            if (openContactFormSidebar || upgradeHotspotsConfig.open) return;

            closeSidebar();
          }}
          initialFocus={dialogRef}
        >
          <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div
              className={classNames(
                "fixed inset-y-0 pl-16 max-w-full right-0 flex duration-500",
                openContactFormSidebar && "pr-40"
              )}
            >
              <div className="absolute inset-0" />
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="relative w-screen max-w-full sm:max-w-md">
                  <ContactFormSidebar
                    contact={undefined}
                    open={openContactFormSidebar}
                    toggleEditContactForm={toggleEditContactForm}
                    showLinkedLocations={false}
                  />

                  <FormProvider
                    classnames={{ wrapper: "xl:max-w-7xl h-full" }}
                    onCancel={() => closeSidebar()}
                    error={errorMessage}
                    disabled={!isAbleToAddLocation}
                    formikProps={{
                      onSubmit,
                      initialValues,
                      validationSchema: Yup.object().shape({
                        name: Yup.string().required("Required"),
                        address: Yup.string(),
                        lat: Yup.number().required("Required"),
                        lng: Yup.number().required("Required"),
                        height: Yup.number().nullable().min(0).max(2800) // 2800ft for the burj khalifa
                      })
                    }}
                    orderedFieldNames={ORDERED_FIELD_NAMES}
                  >
                    {({
                      isSubmitting,
                      setFieldValue,
                      values: formValues,
                      setFieldTouched
                    }: FormikProps<FormValues>) => {
                      return (
                        <div className="flex-1 h-0 overflow-y-auto">
                          <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                              <Dialog.Title className="text-lg font-medium text-white">
                                {config?.location &&
                                isHotspotLocation(config.location) &&
                                config.location.uuid &&
                                config.location
                                  ? "Edit"
                                  : "New"}{" "}
                                location
                              </Dialog.Title>
                              <div className="ml-3 h-7 flex items-center">
                                <button
                                  autoFocus
                                  type="button"
                                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                  onClick={() => closeSidebar()}
                                >
                                  <span className="sr-only">Close panel</span>
                                  <XIcon
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="flex-1 flex flex-col justify-between">
                            <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                              <div className="space-y-6 pt-6 pb-5">
                                {config.show && limitExceededAlertMessage}

                                <Field
                                  label={
                                    <div className="flex items-center justify-between">
                                      <div>Address</div>
                                      <div
                                        onClick={() => {
                                          if (!isAbleToAddLocation) return;

                                          setSelectPointsOnMapConfig({
                                            coordinates:
                                              formValues.lng && formValues.lat
                                                ? [
                                                    formValues.lng,
                                                    formValues.lat
                                                  ]
                                                : undefined
                                          });
                                          history.push("/locations");
                                          closeSidebar({
                                            ...config?.location,
                                            ...formValues,
                                            linked_contact_ids:
                                              linkedContactUuids,
                                            uuid:
                                              config?.location &&
                                              isHotspotLocation(
                                                config?.location
                                              )
                                                ? config?.location.uuid
                                                : null
                                          });
                                        }}
                                        className={classNames(
                                          isAbleToAddLocation
                                            ? "cursor-pointer"
                                            : "cursor-not-allowed opacity-75",
                                          "flex items-center text-sm text-gray-500 dark:text-white"
                                        )}
                                      >
                                        <LocationMarkerIcon className="h-3 w-3 mr-1 text-gray-500 dark:text-white" />
                                        Drop a pin
                                      </div>
                                    </div>
                                  }
                                  name="place"
                                  placeholder="Enter an address"
                                  disabled={
                                    isSubmitting || !isAbleToAddLocation
                                  }
                                  getImageUrl={(place: GeoResult) =>
                                    stringifyUrl({
                                      url: `${avatarsApi}/initials/${getPlaceCountryCode(
                                        place
                                      )}.svg`,
                                      query: { w: 24, h: 24 }
                                    })
                                  }
                                  fetchItems={fetchPlaces}
                                  initialItems={() => {
                                    if (!location) return [];

                                    if (
                                      isHotspotLocation(location) &&
                                      !location.uuid &&
                                      location.address
                                    ) {
                                      return fetchPlaces(location.address);
                                    }

                                    return [];
                                  }}
                                  itemToString={(place: GeoResult) =>
                                    place.name
                                  }
                                  component={DropdownSearch}
                                  validate={(place: GeoResult | undefined) => {
                                    if (
                                      !place &&
                                      !(formValues.lat && formValues.lng)
                                    ) {
                                      return "Required";
                                    }

                                    if (!!config?.location) return;

                                    const lng = place
                                      ? place.center[0]
                                      : formValues.lng;
                                    const lat = place
                                      ? place.center[1]
                                      : formValues.lat;

                                    const existingLocation = find(
                                      values(locations),
                                      { lat, lng }
                                    );

                                    if (existingLocation) {
                                      return `There is already a location for this address, called "${existingLocation.name}"`;
                                    }
                                  }}
                                />

                                <HandleFieldChange<FormValues>
                                  name="place"
                                  onChange={async (
                                    selectedPlace?: GeoResult
                                  ) => {
                                    if (!selectedPlace) {
                                      setFieldValue("name", "");
                                      setFieldTouched("name", true);

                                      setFieldValue("address", "");
                                      setFieldTouched("address", true);

                                      setFieldValue("lat", "");
                                      setFieldTouched("lat", true);

                                      setFieldValue("lng", "");
                                      setFieldTouched("lng", true);

                                      setErrorMessage(null);

                                      return;
                                    }

                                    const generatedFormValues =
                                      convertGeoResultIntoFormValues(
                                        selectedPlace
                                      );

                                    if (!formValues.name) {
                                      setFieldValue(
                                        "name",
                                        generatedFormValues.name
                                      );

                                      setTimeout(() => {
                                        setFieldTouched("name", true);
                                      });
                                    }

                                    if (
                                      location &&
                                      isHotspotLocation(location) &&
                                      !location.uuid &&
                                      location.address
                                    ) {
                                      setFieldValue("name", location.name);
                                    }

                                    setFieldValue(
                                      "address",
                                      generatedFormValues.address
                                    );
                                    setFieldTouched("address", true);

                                    setFieldValue(
                                      "lat",
                                      generatedFormValues.lat
                                    );
                                    setFieldTouched("lat", true);

                                    setFieldValue(
                                      "lng",
                                      generatedFormValues.lng
                                    );
                                    setFieldTouched("lng", true);

                                    setCenter([
                                      generatedFormValues.lng,
                                      generatedFormValues.lat
                                    ]);

                                    setZoom([15]);

                                    document.getElementById("name")?.focus();
                                  }}
                                />

                                {formValues.lat && formValues.lng && (
                                  <AlertMessage
                                    className="mt-4"
                                    type="info"
                                    title="Address details"
                                    message={
                                      <div className="space-y-4">
                                        <Field
                                          id="address"
                                          name="address"
                                          label="Full address"
                                          placeholder="E.g. 767 5th Ave, New York, NY 10153, United States"
                                          type="text"
                                          textarea
                                          rows={3}
                                          component={Input}
                                          disabled={isSubmitting}
                                        />

                                        <Field
                                          id="lat"
                                          name="lat"
                                          label="Latitude"
                                          placeholder="38.7244477"
                                          type="number"
                                          component={Input}
                                          disabled={isSubmitting}
                                        />

                                        <Field
                                          id="lng"
                                          name="lng"
                                          label="Longitude"
                                          placeholder="-9.1584534"
                                          type="number"
                                          component={Input}
                                          disabled={isSubmitting}
                                        />
                                      </div>
                                    }
                                    collapsible
                                  />
                                )}

                                <Field
                                  id="name"
                                  name="name"
                                  label="Name"
                                  placeholder="My rooftop"
                                  type="text"
                                  component={Input}
                                  disabled={
                                    isSubmitting || !isAbleToAddLocation
                                  }
                                />

                                <Field
                                  id="height"
                                  name="height"
                                  label={
                                    <>
                                      Installation height
                                      <Tooltip
                                        placement="top"
                                        overlay="Antenna height above the ground"
                                      >
                                        <InfoIcon className="mx-1 inline-flex h-4 w-4" />
                                      </Tooltip>
                                      ({distanceUnit})
                                    </>
                                  }
                                  placeholder={renderDistance(
                                    DEFAULT_HEIGHT_IN_METERS,
                                    false
                                  )}
                                  type="number"
                                  component={Input}
                                  disabled={
                                    isSubmitting || !isAbleToAddLocation
                                  }
                                />

                                {!config?.parentForm && (
                                  <>
                                    <Field
                                      label={
                                        <div className="flex items-center justify-between">
                                          <div>Contacts</div>
                                          <div
                                            onClick={() =>
                                              isAbleToAddLocation &&
                                              setOpenContactFormSidebar(true)
                                            }
                                            className={classNames(
                                              isAbleToAddLocation
                                                ? "cursor-pointer"
                                                : "cursor-not-allowed opacity-75",
                                              "flex items-center text-sm text-gray-500 dark:text-white"
                                            )}
                                          >
                                            <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
                                            Add new contact
                                          </div>
                                        </div>
                                      }
                                      name="selectedLinkedContact"
                                      placeholder={
                                        isEmpty(contacts)
                                          ? "No contacts available"
                                          : "Search contacts"
                                      }
                                      disabled={
                                        isSubmitting ||
                                        isEmpty(contacts) ||
                                        !isAbleToAddLocation
                                      }
                                      fetchItems={getContacts}
                                      itemToString={(contact: Contact) =>
                                        contact.name
                                      }
                                      component={DropdownSearch}
                                      withoutMinimumSearchCharacters
                                      initialItems={getContacts("")}
                                      onSelectedItem={(
                                        contact: Contact | undefined,
                                        clearInputCallback: () => void
                                      ) => {
                                        if (contact) {
                                          setLinkedContactUuids(
                                            (prevLinkedContactUuids) =>
                                              prevLinkedContactUuids.concat(
                                                contact.uuid
                                              )
                                          );

                                          clearInputCallback();
                                        }
                                      }}
                                    />

                                    {!isEmpty(linkedContactUuids) && (
                                      <div className="flex flex-col w-full shadow overflow-hidden sm:rounded-lg">
                                        <div className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                                          {linkedContactUuids.map((uuid) => (
                                            <div
                                              key={uuid}
                                              className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3"
                                            >
                                              <ContactComponent
                                                contactId={uuid}
                                                withoutLabels
                                              />

                                              <button
                                                className="inline-flex rounded-md p-1.5 focus:outline-none text-hotspotty-400 hover:bg-hotspotty-100 dark:text-white dark:hover:text-gray-800 disabled:opacity-50 disabled:cursor-default"
                                                type="button"
                                                disabled={
                                                  isSubmitting ||
                                                  !isAbleToAddLocation
                                                }
                                                onClick={() => {
                                                  setLinkedContactUuids(
                                                    (prevLinkedContactUuids) =>
                                                      without(
                                                        prevLinkedContactUuids,
                                                        uuid
                                                      )
                                                  );
                                                }}
                                              >
                                                <TrashIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </FormProvider>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default LocationFormSidebar;

//
// Utils
//

interface FormValues {
  name: string;
  place?: GeoResult;
  address: string;
  lat: number;
  lng: number;
  height: number;
}

const ORDERED_FIELD_NAMES = [
  "name",
  "place",
  "address",
  "lat",
  "lng",
  "height"
];

export const DEFAULT_HEIGHT_IN_METERS = 0;

export type LocationType = HotspotLocation | GeoResult | Coordinates;

export interface EditLocationConfig {
  show: boolean;
  location?: LocationType | undefined;
  parentForm?: "contact";
  onSuccess?: (location: LocationType) => void;
}

export const isHotspotLocation = (
  location: LocationType
): location is HotspotLocation => "uuid" in location;

export const mergePlaceResultIntoHotspotLocation = (
  place: GeoResult,
  location: HotspotLocation
): HotspotLocation => {
  return {
    ...location,
    ...convertGeoResultIntoFormValues(place)
  };
};

const convertGeoResultIntoFormValues = (place: GeoResult): FormValues => {
  const [lng, lat] = place.center;
  const { name, address } = place;

  return {
    lng,
    lat,
    name,
    address,
    place,
    height: DEFAULT_HEIGHT_IN_METERS
  };
};

const convertCoordinatesIntoFormValues = ([
  lng,
  lat
]: Coordinates): FormValues => ({
  lng,
  lat,
  name: "Dropped pin",
  address: "",
  place: undefined,
  height: DEFAULT_HEIGHT_IN_METERS
});
